/**
 * Created by camiel on 26/05/2017.
 */

import Bitmap = createjs.Bitmap;
import Container = createjs.Container;
import LoadQueue = createjs.LoadQueue;
import Sound = createjs.Sound;
import AbstractSoundInstance  = createjs.AbstractSoundInstance;
import Tween = createjs.Tween;

import AssetManager from "lib/src/AssetManager";

import IColorOffset, { darken } from "src/IColorOffset";
import Analytics from "lib/src/Analytics";

declare var window: any;

const LOOP_BUTTON_ID = "loop-button";
const LOOP_BUTTON_NOTE_ID = "loop-button-note";

export const LOOP_BUTTON_WIDTH = 150;
export const LOOP_BUTTON_HEIGHT = 154;
export const LOOP_BUTTON_PADDING = 50;

export const LOOP_IDS: ReadonlyArray<string> = Object.freeze([
  "loop1",
  "loop2",
  "loop3",
  "loop4",
]);

export default class LoopButton {

  public static addToManifest(manifest: any[]): void {
    const assets = [
      LOOP_BUTTON_ID,
      LOOP_BUTTON_NOTE_ID,
    ];

    assets
      .map((id) => ({
        id,
        src: AssetManager.getShared(`${id}.png`),
      }))
      .forEach((asset) => manifest.push(asset));

    // Safari does not loop mp3's correctly
    // (tested on iOS 10.3)
    // Also some Android devices will not loop mp'3 without interruption
    // (Samsung S5 with Android 6)
    // So we'll use WAV.

    LOOP_IDS.forEach((loopId) => Sound.registerSound(
      AssetManager.getShared(`sound/loops/${loopId}.wav`),
      loopId,
    ));
  }

  private static _getColorOffset(loopId: string): IColorOffset {
    switch (loopId) {
      case LOOP_IDS[0]: return { red: 221, green: 141, blue: 188 };
      case LOOP_IDS[1]: return { red: 125, green: 208, blue: 240 };
      case LOOP_IDS[2]: return { red: 78, green: 185, blue: 97 };
      default: return { red: 237, green: 237, blue: 70 };
    }
  }

  public readonly container: Container;
  private _buttonContainer: Container;
  private _background: Bitmap;
  private _soundInstance: AbstractSoundInstance;

  constructor(
    public readonly loopId: string,
    private readonly _preloadQueue: LoadQueue,
    private readonly _onClick: (loopId: string) => void,
  ) {
    this.container = new Container();

    this._initializeButton();
  }

  public stopLoop(): void {
    if (this.playing) {
      this._soundInstance.stop();
      this._soundInstance = null;

      this._animatePlaying();
    }
  }

  public playLoop(): void {
    if (!this.playing) {
      this._soundInstance = Sound.play(this.loopId, {
        loop: -1,
      });

      this._animatePlaying();
    }
  }

  public toggleLoop(): void {
    if (this.playing) {
      this.stopLoop();
    } else {
      Analytics.logEvent("keyboard-start-loop", {loop: this.loopId, _label: this.loopId});
      this.playLoop();
    }
  }

  public get playing(): boolean {
    return !!this._soundInstance;
  }

  private _initializeButton(): void {
    this._buttonContainer = new Container();

    const buttonBackgroundImage = this._preloadQueue.getResult(LOOP_BUTTON_ID) as HTMLImageElement;
    this._background = new Bitmap(buttonBackgroundImage);

    this._setBackgroundColor(false);

    this._background.regX = LOOP_BUTTON_WIDTH;
    this._background.regY = buttonBackgroundImage.height / 2;

    this._buttonContainer.addChild(this._background);

    const buttonNoteImage = this._preloadQueue.getResult(LOOP_BUTTON_NOTE_ID) as HTMLImageElement;
    const buttonNote = new Bitmap(buttonNoteImage);

    buttonNote.regX = buttonNoteImage.width / 2;
    buttonNote.regY = buttonNoteImage.height / 2;
    buttonNote.x = -LOOP_BUTTON_WIDTH / 2;

    this._buttonContainer.addChild(buttonNote);

    this._buttonContainer.on("click", () => {
      this._onClick(this.loopId);
    });

    this.container.addChild(this._buttonContainer);
  }

  private _setBackgroundColor(dark: boolean): void {
    const colorOffset = darken(LoopButton._getColorOffset(this.loopId), dark ? 0.1 : 0.0);

    this._background.filters = [
      new createjs.ColorFilter(0, 0, 0, 1, colorOffset.red, colorOffset.green, colorOffset.blue, 0),
    ];
    this._background.cache(0, 0, this._background.image.width, this._background.image.height);
  }

  private _animatePlaying(): void {
    const scale = this.playing ? 1.2 : 1;

    this._setBackgroundColor(this.playing);

    Tween
      .get(this._buttonContainer)
      .to({ scaleX: scale, scaleY: scale }, 100);
  }
}



// WEBPACK FOOTER //
// ./src/LoopButton.ts